// ============================================================================
// 시뮬레이션 — 다섯 단계의 물리 상태. 고정 간격(240Hz)으로 갱신. 이징 커브 없음.
//   pack → cutting → opening → stack → flipping → reveal → done
// 렌더러는 RS(렌더 상태) 만 읽는다. prev/cur 를 보간해 그린다.
// ============================================================================
import { P, WORLD, CARD_DRAW, CARD_COUNT, rollCards } from './params';
import { PointerInput, Vec2 } from './input';
import { audio } from './audio';
import { haptics } from './haptics';

export type Phase = 'pack' | 'cutting' | 'opening' | 'stack' | 'flipping' | 'reveal' | 'done';
export type SwipeState = 'idle' | 'stuck' | 'slip' | 'return' | 'fly';

// ------------------------------------------------------------------ 노이즈
function hash(i: number, seed: number): number {
  let h = (Math.imul(i | 0, 374761393) + Math.imul(seed | 0, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}
export function noise1(x: number, seed: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const s = f * f * (3 - 2 * f);
  return hash(i, seed) * (1 - s) + hash(i + 1, seed) * s;
}
export function fbm(x: number, seed: number): number {
  return 0.7 * noise1(x, seed) + 0.3 * noise1(x * 2.7 + 13.1, seed + 1);
}

// ------------------------------------------------------------------ 렌더 상태
export class RS {
  topX = 0;
  topY = 0;
  topRot = 0;
  topVis = 1;
  bodyY = 0;
  cutFront = -1;
  cutStartD = 0;
  cutDir = 0;
  cutStartEdgeX = 0;
  gapScale = 1;
  bulgeD = 0;
  bulgeAmt = 0;
  tension = 0;
  stackX = 0;
  stackY = 0;
  stackZ = 0;
  flipMode = 0;
  flipTheta = 0;
  flipTilt = 0;
  roll = 0;
  cx = new Float64Array(CARD_COUNT);
  cy = new Float64Array(CARD_COUNT);
  cz = new Float64Array(CARD_COUNT);
  crot = new Float64Array(CARD_COUNT);
  cbend = new Float64Array(CARD_COUNT);
  ccontact = new Float64Array(CARD_COUNT); // 접점의 축 방향 투영 (카드 중심 기준, unit)
  cbdx = new Float64Array(CARD_COUNT); // 휘어짐 축 (world)
  cbdy = new Float64Array(CARD_COUNT);
  cvis = new Float64Array(CARD_COUNT);
  // 떠오르는 금액 텍스트 (`-$4.49`). floatKey 0 = 안 보임. 글자와 부호는 sim.float 에서 읽는다
  // (문자열이라 보간할 것이 없다 — key 가 바뀔 때만 렌더가 텍스처를 다시 만든다).
  floatKey = 0;
  floatX = 0;
  floatY = 0;
  floatDY = 0;
  floatA = 0;
  // AR·UR 등장 연출 (P.reveal). fxIdx < 0 = 연출 없음.
  // **위치·크기는 없다** — 연출은 그 카드 앞면 셰이더의 색만 건드린다 (손맛 보호, params.ts 주석).
  fxIdx = -1;
  fxRank = -1;
  fxWidth = 0.1;
  fxBand = -10;
  fxStrength = 0;
  fxFlash = 0;

  static SCALARS = ['topX', 'topY', 'topRot', 'bodyY', 'cutFront', 'gapScale', 'bulgeD', 'bulgeAmt', 'tension', 'stackX', 'stackY', 'stackZ', 'flipTheta', 'flipTilt', 'roll', 'floatDY', 'floatA', 'fxBand', 'fxStrength', 'fxFlash'] as const;
  static DISCRETE = ['topVis', 'cutStartD', 'cutDir', 'cutStartEdgeX', 'flipMode', 'floatKey', 'floatX', 'floatY', 'fxIdx', 'fxRank', 'fxWidth'] as const;
  static ARRAYS = ['cx', 'cy', 'cz', 'crot', 'cbend', 'ccontact', 'cbdx', 'cbdy'] as const;

  copyFrom(o: RS): void {
    for (const k of RS.SCALARS) this[k] = o[k];
    for (const k of RS.DISCRETE) this[k] = o[k];
    for (const k of RS.ARRAYS) this[k].set(o[k]);
    this.cvis.set(o.cvis);
  }

  /** this = lerp(a, b, t). 이산값과 모드 전환 프레임은 b 를 그대로 쓴다. */
  lerp(a: RS, b: RS, t: number): void {
    const snap = a.flipMode !== b.flipMode;
    for (const k of RS.SCALARS) this[k] = snap ? b[k] : a[k] + (b[k] - a[k]) * t;
    for (const k of RS.DISCRETE) this[k] = b[k];
    for (const k of RS.ARRAYS) {
      const A = a[k], B = b[k], O = this[k];
      for (let i = 0; i < CARD_COUNT; i++) O[i] = snap || a.cvis[i] !== b.cvis[i] ? B[i] : A[i] + (B[i] - A[i]) * t;
    }
    this.cvis.set(b.cvis);
  }
}

interface Flyer {
  idx: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  w: number;
  bend: number;
  contactProj: number;
  dirX: number;
  dirY: number;
  revealed: boolean;
}

function dampingFromOvershoot(os: number): number {
  if (os <= 0.0005) return 1;
  const l = Math.log(Math.min(0.95, os));
  return -l / Math.sqrt(Math.PI * Math.PI + l * l);
}
function omegaFromDuration(T: number, zeta: number): number {
  if (zeta >= 0.999) return 4.7 / T;
  const s = Math.sqrt(1 - zeta * zeta);
  return (Math.PI - Math.atan(s / zeta)) / (s * T);
}

// ------------------------------------------------------------------ 시뮬
export class Sim {
  phase: Phase = 'pack';
  rs = new RS();
  prev = new RS();
  time = 0;

  // 자르기
  cut = {
    active: false,
    dir: 0,
    startEdgeX: 0,
    startD: 0,
    front: 0,
    downX: 0,
    entryD: 0,
    entryAmp: 1,
    moving: false,
    penetratedThisStroke: false,
    seed: 1,
    jagSeed: 2,
    F: 0,
    Rk: 0,
    Rs: 0,
    vb: 0,
    lag: 0,
    bulgeSmooth: 0,
    lastVelX: 0,
    completed: false,
    hapticAcc: 0, // 연속 진동 펄스 스케줄러
  };
  // 개봉
  open = {
    t: 0,
    topVy: 0,
    topVx: 0,
    topSpin: 0,
    bodyV: 0,
    cardRise: new Float64Array(CARD_COUNT),
    cardV: new Float64Array(CARD_COUNT),
    cardStarted: new Uint8Array(CARD_COUNT),
  };
  // 뒤집기
  flip = { theta: 0, omega: 0, tilt: 0, crossed: false, roll: 0, rollV: 0, t: 0 };
  // 넘기기
  // 카드는 2차원으로 자유롭게 끌린다. 축 고정 없음 — 손가락이 가는 대로 따라온다.
  sw = {
    state: 'idle' as SwipeState,
    top: 0,
    px: 0, // 카드 오프셋 (rest 기준, world)
    py: 0,
    vx: 0, // 카드 속도
    vy: 0,
    bend: 0, // |목표 − 현재| = 손가락이 카드보다 앞선 거리
    bdx: 0, // 지연/휘어짐 방향 (단위 벡터)
    bdy: 1,
    grabX: 0, // 잡은 지점 (카드 중심 기준)
    grabY: 0,
    contactProj: 0, // 접점을 지연 방향에 투영한 값
    latOff: 0,
    F: 0,
    Rs: 0,
    Rk: 0,
    bMax: 0,
    flyers: [] as Flyer[],
    slipCount: 0,
    lastReleaseVel: { x: 0, y: 0 } as Vec2,
    // 두근두근 (뒤 카드가 드러나는 동안)
    reveal: 0, // 다음 카드가 드러난 비율 0..1
    revealIdx: -1, // 드러나는 중인 카드 index
    rareAcc: 0, // 연속 펄스 스케줄러
    rareT: 0, // 진행도 0..1 (소리 고조 + 진단)
    rareActive: false,
    rarePulses: 0,
    // 등장 연출이 터진 카드 index. 여기까지의 예고 리저는 **끝난 것**이라 다시 울리지 않는다
    // (크레셴도 뒤에 타격이 오고, 타격 뒤에 리저가 되살아나면 앞뒤가 뒤집힌다).
    rareCutIdx: -1,
  };

  /** AR·UR 등장 연출. idx < 0 = 없음. 진행도 t 는 240Hz 로 적분된다 (CSS animation 아님). */
  fx = { idx: -1, rank: -1, t: 0, scale: 1 };
  spreadX = new Float64Array(CARD_COUNT);
  spreadY = new Float64Array(CARD_COUNT);
  spreadR = new Float64Array(CARD_COUNT);

  // 떠오르는 금액 텍스트. sim 이 상태를 들고 RS 로 넘기고 render 가 그린다 (CSS animation 아님).
  // 글자·부호는 **여기서** 산다 — 금액 표기는 econ 의 일이고 sim 은 문자열을 그대로 받는다.
  float = { active: false, t: 0, key: 0, x: 0, y: 0, text: '', sign: -1 };

  /**
   * 금액 텍스트를 띄운다. `sign` 은 색만 정한다 (-1 차감 = 붉은색 / +1 획득 = 초록색).
   * 자르기 결제(main.ts `chargeForCut`)가 부른다. 판매(+$N)에도 그대로 쓸 수 있다.
   */
  showFloat(text: string, sign = -1, x = 0, y = P.econ.floatY): void {
    const f = this.float;
    f.active = true;
    f.t = 0;
    f.key = (f.key % 1000000) + 1;
    f.x = x;
    f.y = y;
    f.text = text;
    f.sign = sign;
  }

  /** 화면을 떠날 때 부른다. 안 끄면 바인더에 갔다 와도 이전 금액이 떠 있다. */
  clearFloat(): void {
    this.float.active = false;
  }

  private pendingDown: { p: Vec2; t: number } | null = null;
  private pendingUp: { p: Vec2; t: number; v: Vec2 } | null = null;
  private seedCounter = 1;

  // 이벤트 훅 (렌더러/진단용)
  onEvent: ((name: string) => void) | null = null;

  /**
   * **자르기 결제 게이트.** main.ts 가 붙인다. 팩이 실제로 찢어지기 시작하는 첫 프레임
   * (= 방향이 확정되는 순간, 아래 stepCut) 직전에 딱 한 번 불린다.
   * false 를 돌려주면 자르기가 아예 시작되지 않고 팩은 그대로 놓여 있다.
   * sim 은 돈을 모른다 — econ.ts 도 save.ts 도 여기서 import 하지 않는다.
   */
  onCutCharge: (() => boolean) | null = null;

  constructor(public input: PointerInput) {
    // **입력 콜백을 여기서 잡지 않는다.** 화면이 둘이 되면서 main.ts 가 활성 sim 에만 전달한다.
    // 그대로 두면 바인더에서 누른 탭이 pendingDown 에 남았다가 돌아오는 순간 팩을 자르기 시작한다.
    this.reset();
  }

  /** main.ts 가 이 화면이 활성일 때만 부른다. 접촉 틱은 자기 히트테스트로 결정한다. */
  pointerDown(p: Vec2, t: number): void {
    this.pendingDown = { p, t };
    // 20ms 예산: 이벤트 시각에 즉시 첫 소리
    if (this.hitTestForTouchTick(p)) audio.touchTick();
  }
  pointerUp(p: Vec2, t: number, v: Vec2): void {
    this.pendingUp = { p, t, v };
  }
  /** 화면을 나갈 때 큐를 비운다 (입력 누수 방지). */
  clearPending(): void {
    this.pendingDown = null;
    this.pendingUp = null;
  }

  get perfY(): number {
    return WORLD.packH / 2 - P.cut.perfFrac * WORLD.packH;
  }

  /** 절단선 프로파일 (팩 로컬 y). 매 리셋마다 다른 모양. */
  jagY = (x: number): number => {
    return this.perfY + P.cut.lineJitter * fbm(x * P.cut.lineJitterFreq, this.cut.jagSeed);
  };

  private rnd(): number {
    this.seedCounter = (this.seedCounter * 1664525 + 1013904223) >>> 0;
    return this.seedCounter / 4294967296;
  }

  reset(): void {
    // 리셋마다 8장을 다시 뽑는다. 시드(?seed)가 지정됐으면 매번 같은 8장이 나온다.
    rollCards();
    this.phase = 'pack';
    this.time = 0;
    const c = this.cut;
    c.active = false;
    c.dir = 0;
    c.startEdgeX = 0;
    c.startD = 0;
    c.front = 0;
    c.entryD = 0;
    c.entryAmp = 1;
    c.moving = false;
    c.penetratedThisStroke = false;
    c.seed = (Date.now() ^ (this.seedCounter * 7919)) | 0;
    c.jagSeed = c.seed + 101;
    c.F = c.Rk = c.Rs = c.vb = c.lag = 0;
    c.bulgeSmooth = 0;
    c.completed = false;
    c.hapticAcc = 0;
    haptics.stop();
    const o = this.open;
    o.t = 0;
    o.topVy = o.topVx = o.topSpin = o.bodyV = 0;
    o.cardRise.fill(-P.open.cardStartDepth);
    o.cardV.fill(0);
    o.cardStarted.fill(0);
    this.flip = { theta: 0, omega: 0, tilt: 0, crossed: false, roll: 0, rollV: 0, t: 0 };
    // 팩 조각 위치 복원 (상단 조각 표시, 하단 몸통 제자리)
    this.topX = 0;
    this.topY = 0;
    this.topRot = 0;
    this.topVis = true;
    this.bodyY = 0;
    const s = this.sw;
    s.state = 'idle';
    s.top = 0;
    s.px = s.py = s.vx = s.vy = s.bend = 0;
    s.bdx = 0;
    s.bdy = 1;
    s.contactProj = s.latOff = 0;
    s.flyers = [];
    s.slipCount = 0;
    s.reveal = 0;
    s.revealIdx = -1;
    s.rareAcc = 0;
    s.rareT = 0;
    s.rareActive = false;
    s.rarePulses = 0;
    s.rareCutIdx = -1;
    this.fx.idx = -1;
    this.fx.rank = -1;
    this.fx.t = 0;
    this.fx.scale = 1;
    for (let k = 0; k < CARD_COUNT; k++) {
      this.spreadX[k] = (this.rnd() * 2 - 1) * P.open.spreadPos;
      this.spreadY[k] = (this.rnd() * 2 - 1) * P.open.spreadPos;
      this.spreadR[k] = (this.rnd() * 2 - 1) * P.open.spreadAngle;
    }
    this.pendingDown = null;
    this.pendingUp = null;
    // 리셋에서 금액 텍스트도 지운다 (안 지우면 새 팩 위에 이전 팩의 -$4.49 가 남는다).
    this.float.active = false;
    this.float.t = 0;
    audio.silenceContinuous();
    this.writeRS();
    this.prev.copyFrom(this.rs);
    this.onEvent?.('reset');
  }

  // ---------------------------------------------------------------- 히트 테스트
  private inCutBand(p: Vec2): boolean {
    return Math.abs(p.y - this.perfY) <= P.cut.bandHalf && Math.abs(p.x) <= WORLD.packW / 2 + 0.4;
  }
  private inStackRect(p: Vec2, margin = 0.3): boolean {
    return Math.abs(p.x) <= WORLD.cardW / 2 + margin && Math.abs(p.y - P.open.stackY) <= WORLD.cardH / 2 + margin;
  }
  /** 맨 위 카드의 rest 중심 (world). 드래그 오프셋은 포함하지 않는다. */
  private topCardRest(): Vec2 {
    const k = Math.min(CARD_COUNT - 1, this.sw.top);
    return { x: this.spreadX[k], y: P.open.stackY - this.spreadY[k] };
  }

  /** 카드 사각형의 방향 dir 로의 반경 (중심에서 가장자리까지) */
  private extentAlong(dx: number, dy: number): number {
    return (WORLD.cardW / 2) * Math.abs(dx) + (WORLD.cardH / 2) * Math.abs(dy);
  }
  hitTestForTouchTick(p: Vec2): boolean {
    if (this.phase === 'pack' || this.phase === 'cutting') return this.inCutBand(p);
    if (this.phase === 'reveal') return this.inStackRect(p);
    return false;
  }

  // ---------------------------------------------------------------- 스텝
  step(h: number, simT: number): void {
    this.time += h;
    // 금액 텍스트는 **단계와 무관하게** 흐른다 (뜯는 순간 phase 가 pack→cutting 으로 바뀌므로).
    if (this.float.active) {
      this.float.t += h;
      if (this.float.t >= Math.max(0.05, P.econ.floatDur)) this.float.active = false;
    }
    if (this.pendingDown && this.pendingDown.t <= simT + 0.5) {
      this.handleDown(this.pendingDown.p);
      this.pendingDown = null;
    }
    if (this.pendingUp && this.pendingUp.t <= simT + 0.5) {
      this.handleUp(this.pendingUp.v);
      this.pendingUp = null;
    }
    switch (this.phase) {
      case 'pack':
        break;
      case 'cutting':
        this.stepCut(h);
        break;
      case 'opening':
        this.stepOpen(h);
        break;
      case 'stack':
        this.stepOpenTail(h);
        break;
      case 'flipping':
        this.stepOpenTail(h);
        this.stepFlip(h);
        break;
      case 'reveal':
      case 'done':
        this.stepOpenTail(h);
        this.stepRoll(h);
        this.stepSwipe(h);
        this.stepRevealFx(h);
        break;
    }
    this.writeRS();
  }

  private handleDown(p: Vec2): void {
    switch (this.phase) {
      case 'pack':
      case 'cutting':
        if (this.inCutBand(p)) {
          this.phase = 'cutting';
          const c = this.cut;
          c.active = true;
          c.downX = p.x;
          c.moving = false;
          c.penetratedThisStroke = false;
          if (c.dir !== 0) {
            // 이어서 자르기: 재진입 저항
            c.entryD = c.front;
            c.entryAmp = c.front > c.startD + 1e-3 ? P.cut.reentryFactor : 1;
          }
        }
        break;
      case 'stack':
        if (this.inStackRect(p, 0.6)) this.startFlip(p);
        break;
      case 'reveal':
        if (this.sw.top < CARD_COUNT && this.inStackRect(p, 0.25)) this.grabCard(p);
        break;
      default:
        break;
    }
  }

  private handleUp(v: Vec2): void {
    if (this.phase === 'cutting') {
      this.cut.active = false;
      this.cut.moving = false;
      this.cut.F = 0;
      this.cut.vb = 0;
      this.cut.hapticAcc = 0;
      audio.setCut(0, 0);
      audio.tickCrackle(0, 0);
      haptics.stop(); // 손을 떼면 진동도 즉시 끊는다
    } else if (this.phase === 'reveal') {
      const s = this.sw;
      if (s.state === 'stuck' || s.state === 'slip') this.releaseCard(v);
    }
  }

  // ---------------------------------------------------------------- 자르기
  private stepCut(h: number): void {
    const c = this.cut;
    const w = WORLD.packW;
    const inp = this.input;
    if (!c.active) {
      c.F = 0;
      c.vb = 0;
      c.hapticAcc = 0;
      c.bulgeSmooth += (0 - c.bulgeSmooth) * (1 - Math.exp(-h * 40));
      return;
    }
    if (c.dir === 0) {
      const dx = inp.pos.x - c.downX;
      if (Math.abs(dx) < P.cut.directionDeadzone) return;
      // ---- **결제 지점.** 여기가 "팩을 뜯기 시작한" 첫 프레임이다.
      // 절취선 띠 안에서 손가락이 directionDeadzone 만큼 실제로 그어졌다는 뜻이고,
      // 바로 아래에서 front 가 서면서 팩이 찢어진 모양으로 그려지기 시작한다.
      // 화면을 그냥 터치한 것으로는 여기까지 안 온다 (handleDown 은 큐에 담기만 한다).
      if (this.onCutCharge && !this.onCutCharge()) {
        // 값을 못 치렀다 — 자르기를 시작하지 않고 팩을 그대로 되돌린다.
        c.active = false;
        this.phase = 'pack';
        audio.setCut(0, 0);
        this.onEvent?.('cut-denied');
        return;
      }
      c.dir = dx > 0 ? 1 : -1;
      c.startEdgeX = c.dir > 0 ? -w / 2 : w / 2;
      c.startD = Math.max(0, Math.min(w, (c.downX - c.startEdgeX) * c.dir));
      c.front = c.startD;
      c.entryD = c.startD;
      c.entryAmp = 1;
      this.onEvent?.('cut-start');
    }
    const df = (inp.pos.x - c.startEdgeX) * c.dir;
    const lag = df - c.front;
    c.lag = lag;
    const F = lag > 0 ? P.cut.bladeStiffness * lag : 0;
    const speed = Math.abs(inp.vFast.x);
    let Rk = P.cut.baseResistance * (1 + P.cut.jitterAmount * fbm(c.front * P.cut.jitterFreq, c.seed)) * (1 + P.cut.velEffect * speed);
    Rk = Math.max(Rk, P.cut.baseResistance * 0.05);
    const pd = Math.max(0.01, P.cut.penetrationDepth);
    const into = c.front - c.entryD;
    const pen = into < pd ? P.cut.penetrationResistance * c.entryAmp * (1 - into / pd) : 0;
    Rk += pen;
    const Rs = Rk * P.cut.stickRatio;
    c.F = F;
    c.Rk = Rk;
    c.Rs = Rs;

    if (!c.moving && F > Rs) {
      c.moving = true;
      if (!c.penetratedThisStroke && pen > 0) {
        c.penetratedThisStroke = true;
        audio.penetrate();
        haptics.pulse(P.haptic.penetrate);
        this.onEvent?.('penetrate');
      }
    }
    if (c.moving) {
      const vb = (F - Rk) / Math.max(0.02, P.cut.bladeDamping);
      if (vb <= 0) {
        c.vb = 0;
        c.moving = false;
      } else {
        c.vb = vb;
        c.front += vb * h;
        if (c.front >= w) {
          c.front = w;
          this.completeCut();
          return;
        }
      }
    } else {
      c.vb = 0;
    }
    const sMax = P.sound.cut.speedForMax;
    audio.setCut(c.vb, Math.min(1, speed / sMax));
    audio.tickCrackle(Math.min(1, c.vb / sMax), h);
    // 연속 진동 — 칼날이 실제로 움직이는 동안에만. 손이 멈추면 소리처럼 진동도 멈춘다.
    // 빈도가 높아 간격이 펄스 길이보다 짧아지면 모터가 안 끊겨 "계속 오는" 진동이 된다.
    const hp = P.haptic;
    if (hp.cutContinuous && c.moving && c.vb >= hp.cutMinSpeed) {
      const t = Math.min(1, c.vb / Math.max(0.1, hp.cutSpeedForMax));
      const rate = hp.cutRateMin + (hp.cutRateMax - hp.cutRateMin) * t;
      c.hapticAcc += rate * h;
      if (c.hapticAcc >= 1) {
        c.hapticAcc -= Math.floor(c.hapticAcc); // 한 스텝에 여러 번 쏘지 않는다
        haptics.pulse(hp.cutPulse);
      }
    }
    // 걸림(stick) 구간에서는 누적을 지우지 않는다. 지우면 느린 드래그처럼
    // 멈춤과 튐이 반복되는 동안 누적이 1 에 못 닿아 진동이 거의 안 난다.
    const press = Math.min(1, F / Math.max(0.2, Rs));
    c.bulgeSmooth += (press - c.bulgeSmooth) * (1 - Math.exp(-h * 60));
  }

  private completeCut(): void {
    const c = this.cut;
    c.completed = true;
    c.active = false;
    c.moving = false;
    c.F = 0;
    c.vb = 0;
    c.lastVelX = this.input.vFast.x;
    audio.setCut(0, 0);
    audio.tickCrackle(0, 0);
    audio.complete();
    haptics.pulse(P.haptic.complete);
    this.onEvent?.('complete');
    // 개봉 시작
    const o = this.open;
    o.t = 0;
    o.topVy = P.open.topFlySpeed;
    o.topVx = P.open.topDriftFactor * c.lastVelX;
    o.topSpin = c.dir * P.open.topSpin;
    o.bodyV = 0;
    o.cardRise.fill(-P.open.cardStartDepth);
    o.cardV.fill(0);
    o.cardStarted.fill(0);
    this.topX = 0;
    this.topY = 0;
    this.topRot = 0;
    this.topVis = true;
    this.bodyY = 0;
    this.phase = 'opening';
  }

  // ---------------------------------------------------------------- 개봉
  private topX = 0;
  private topY = 0;
  private topRot = 0;
  private topVis = true;
  private bodyY = 0;

  private stepOpenTail(h: number): void {
    // 상단 조각 비행 + 하단 정착은 단계가 넘어가도 계속 계산
    const o = this.open;
    const hs = h / Math.max(0.05, P.open.timeScale);
    if (this.topVis) {
      this.topY += o.topVy * hs;
      this.topX += o.topVx * hs;
      o.topVy -= P.open.topGravity * hs;
      this.topRot += o.topSpin * hs;
      const centerY = WORLD.packH / 2 - (P.cut.perfFrac * WORLD.packH) / 2; // 상단 조각 중심의 팩 로컬 y
      if (centerY + this.topY > WORLD.viewH / 2 + 3.2 || centerY + this.topY < -WORLD.viewH / 2 - 3.2) this.topVis = false;
    }
    const target = -P.open.bodySinkDistance;
    const wn = P.open.bodySinkSpeed, z = P.open.bodySinkDamping;
    const acc = wn * wn * (target - this.bodyY) - 2 * z * wn * o.bodyV;
    o.bodyV += acc * hs;
    this.bodyY += o.bodyV * hs;
  }

  private stepOpen(h: number): void {
    const o = this.open;
    const hs = h / Math.max(0.05, P.open.timeScale);
    o.t += hs;
    this.stepOpenTail(h);
    let allSettled = true;
    const wn = P.open.cardRiseSpeed, z = P.open.cardRiseDamping;
    for (let k = 0; k < CARD_COUNT; k++) {
      const delay = P.open.cardDelay + (CARD_COUNT - 1 - k) * P.open.cardStagger;
      if (!o.cardStarted[k]) {
        if (o.t >= delay) o.cardStarted[k] = 1;
        else {
          allSettled = false;
          continue;
        }
      }
      const acc = wn * wn * (0 - o.cardRise[k]) - 2 * z * wn * o.cardV[k];
      o.cardV[k] += acc * hs;
      o.cardRise[k] += o.cardV[k] * hs;
      if (Math.abs(o.cardRise[k]) > 0.03 || Math.abs(o.cardV[k]) > 0.4) allSettled = false;
    }
    if (allSettled || o.t > 2.5) {
      this.phase = 'stack';
      this.onEvent?.('stack');
    }
  }

  // ---------------------------------------------------------------- 뒤집기
  private startFlip(p: Vec2): void {
    const f = this.flip;
    const halfW = (WORLD.viewH * this.aspect) / 2;
    f.tilt = -P.flip.tapInfluence * Math.max(-1, Math.min(1, p.x / Math.max(0.1, halfW)));
    f.theta = 0;
    const zeta = dampingFromOvershoot(P.flip.overshoot);
    const wn = omegaFromDuration(Math.max(0.05, P.flip.duration), zeta);
    f.omega = 0.15 * wn * Math.PI; // 즉시 반응하도록 작은 초기 킥
    f.crossed = false;
    f.t = 0;
    this.phase = 'flipping';
    this.onEvent?.('flip');
  }

  aspect = 0.5;

  private stepFlip(h: number): void {
    const f = this.flip;
    f.t += h;
    const zeta = dampingFromOvershoot(P.flip.overshoot);
    const wn = omegaFromDuration(Math.max(0.05, P.flip.duration), zeta);
    const acc = wn * wn * (Math.PI - f.theta) - 2 * zeta * wn * f.omega;
    f.omega += acc * h;
    f.theta += f.omega * h;
    if (!f.crossed && f.theta >= Math.PI) {
      f.crossed = true;
      audio.thud(1);
      haptics.pulse(P.haptic.land);
      this.onEvent?.('flip-land');
    }
    const settled = f.crossed && Math.abs(f.theta - Math.PI) < 0.003 && Math.abs(f.omega) < 0.1;
    if (settled || f.t > P.flip.duration * 6 + 1) {
      f.theta = Math.PI;
      f.omega = 0;
      f.roll = 2 * f.tilt;
      f.rollV = 0;
      this.phase = 'reveal';
      this.sw.state = 'idle';
      this.onEvent?.('reveal');
      // 첫 장의 앞면이 방금 카메라를 향했다. 기본 규칙(rareLateSlots=2)에서는 여기 AR·UR 이 올 수 없지만
      // 슬라이더로 제약을 풀면(0..CARD_COUNT) 올 수 있다 — 그때도 연출이 나가야 한다.
      this.fireReveal(0);
    }
  }

  private stepRoll(h: number): void {
    const f = this.flip;
    const wn = P.flip.rollReturnSpeed, z = 0.9;
    const acc = wn * wn * (0 - f.roll) - 2 * z * wn * f.rollV;
    f.rollV += acc * h;
    f.roll += f.rollV * h;
  }

  // ---------------------------------------------------------------- 넘기기
  private grabCard(p: Vec2): void {
    const s = this.sw;
    const rest = this.topCardRest();
    // 잡은 지점을 카드의 "현재" 위치 기준으로 기록한다. 그래야 복귀 중에 다시 잡아도
    // 카드가 튀지 않고 그 자리에서 이어서 끌린다.
    s.grabX = p.x - (rest.x + s.px);
    s.grabY = p.y - (rest.y + s.py);
    s.vx = 0;
    s.vy = 0;
    s.bend = 0;
    s.state = 'stuck';
    this.onEvent?.('grab');
  }

  private releaseCard(v: Vec2): void {
    const s = this.sw;
    const sp = P.swipe;
    s.lastReleaseVel = { ...v };
    const disp = Math.hypot(s.px, s.py);
    const vRel = Math.hypot(v.x, v.y);
    if (disp + s.bend >= sp.threshold || vRel >= sp.flickSpeed) {
      // 날아가는 방향: 뗄 때의 손가락 방향이 우선. 거의 멈춰서 뗐으면 끌려온 방향.
      let fx: number, fy: number;
      if (vRel > 0.5) {
        fx = v.x / vRel;
        fy = v.y / vRel;
      } else if (disp > 1e-3) {
        fx = s.px / disp;
        fy = s.py / disp;
      } else {
        fx = s.bdx;
        fy = s.bdy;
      }
      const speed = Math.max(vRel, Math.hypot(s.vx, s.vy), sp.flyMinSpeed);
      const ppx = -fy, ppy = fx; // 비행 방향에 수직
      const latOff = s.grabX * ppx + s.grabY * ppy;
      const perpHalf = Math.max(0.1, this.extentAlong(ppx, ppy));
      const spin = -sp.flySpin * Math.max(-1, Math.min(1, latOff / perpHalf)) * speed;
      s.flyers.push({
        idx: s.top,
        x: s.px,
        y: s.py,
        vx: speed * fx,
        vy: speed * fy,
        rot: 0,
        w: spin,
        bend: s.bend,
        contactProj: s.grabX * fx + s.grabY * fy,
        dirX: fx,
        dirY: fy,
        revealed: false,
      });
      s.top++;
      s.px = s.py = s.vx = s.vy = s.bend = 0;
      s.state = 'idle';
      audio.setFriction(0);
      this.onEvent?.('fly');
    } else {
      s.state = 'return';
      this.onEvent?.('return');
    }
  }

  private stepSwipe(h: number): void {
    const s = this.sw;
    const inp = this.input;
    const sp = P.swipe;

    if (s.state === 'stuck' || s.state === 'slip') {
      // --- 목표 = 잡은 지점이 손가락 아래에 오도록 하는 카드 위치. 방향 제한 없음.
      const rest = this.topCardRest();
      const tx = inp.pos.x - rest.x - s.grabX;
      const ty = inp.pos.y - rest.y - s.grabY;
      let dx = tx - s.px, dy = ty - s.py;
      let b = Math.hypot(dx, dy);
      if (b > 1e-6) {
        s.bdx = dx / b;
        s.bdy = dy / b;
      }
      // 접점을 지연 방향으로 투영 — 그 방향으로 카드가 얼마나 남았는지가 휘어짐 한계를 정한다
      s.contactProj = s.grabX * s.bdx + s.grabY * s.bdy;
      s.latOff = s.grabX * -s.bdy + s.grabY * s.bdx;
      const La = Math.max(0.05, this.extentAlong(s.bdx, s.bdy) - s.contactProj);
      const bMax = Math.max(0.01, Math.min(sp.bendLimit, sp.bendLimitFrac * La));
      s.bMax = bMax;

      let forced = false;
      if (b > bMax) {
        // 한계를 넘으면 카드 전체가 끌려온다
        s.px = tx - s.bdx * bMax;
        s.py = ty - s.bdy * bMax;
        dx = s.bdx * bMax;
        dy = s.bdy * bMax;
        b = bMax;
        forced = true;
      }
      const F = sp.bendStiffness * b;
      s.F = F;
      s.Rs = sp.stickResistance;
      s.Rk = sp.slipResistance;
      if (s.state === 'stuck' && (F > sp.stickResistance || forced)) {
        s.state = 'slip';
        s.slipCount++;
        if (forced) {
          s.vx = inp.vFast.x;
          s.vy = inp.vFast.y;
        }
        audio.clickRelease(forced ? 1 : F / Math.max(0.05, sp.stickResistance));
        haptics.pulse(P.haptic.slip);
        this.onEvent?.('slip');
      }
      if (s.state === 'slip') {
        const m = Math.max(0.0005, sp.mass);
        // 1) 스프링(손가락이 당기는 힘) + 점성 감쇠. 감쇠는 반음시라 h 가 커도 안 터진다.
        const c = 2 * sp.slipDamping * Math.sqrt(sp.bendStiffness * m);
        s.vx = (s.vx + (sp.bendStiffness * dx / m) * h) / (1 + (c / m) * h);
        s.vy = (s.vy + (sp.bendStiffness * dy / m) * h) / (1 + (c / m) * h);
        // 2) 쿨롱 마찰 — 속도 크기만 깎는다. 방향을 뒤집지 않아야 떨림이 안 생긴다.
        const spd = Math.hypot(s.vx, s.vy);
        const dv = (sp.slipResistance / m) * h;
        if (spd <= dv) {
          s.vx = 0;
          s.vy = 0;
        } else {
          const f = (spd - dv) / spd;
          s.vx *= f;
          s.vy *= f;
        }
        s.px += s.vx * h;
        s.py += s.vy * h;
        // 3) 손가락-카드 접촉은 미끄러지지 않는다: 카드는 손가락을 앞지르지 못한다
        const ndx = tx - s.px, ndy = ty - s.py;
        if (ndx * dx + ndy * dy < 0) {
          s.px = tx;
          s.py = ty;
          s.vx = inp.vFast.x;
          s.vy = inp.vFast.y;
        }
        b = Math.hypot(tx - s.px, ty - s.py);
        if (Math.hypot(s.vx, s.vy) < 1e-3 && sp.bendStiffness * b < sp.stickResistance) {
          s.state = 'stuck';
          s.vx = 0;
          s.vy = 0;
        }
      }
      s.bend = b;
      audio.setFriction(Math.hypot(s.vx, s.vy));
    } else if (s.state === 'return') {
      const wn = sp.returnSpeed, z = sp.returnBounce;
      s.vx += (wn * wn * -s.px - 2 * z * wn * s.vx) * h;
      s.vy += (wn * wn * -s.py - 2 * z * wn * s.vy) * h;
      s.px += s.vx * h;
      s.py += s.vy * h;
      s.bend *= Math.exp(-sp.bendRelaxSpeed * h);
      const spd = Math.hypot(s.vx, s.vy);
      audio.setFriction(spd * 0.6);
      if (Math.hypot(s.px, s.py) < 0.003 && spd < 0.05) {
        s.px = s.py = s.vx = s.vy = 0;
        s.state = 'idle';
        audio.setFriction(0);
      }
    } else {
      audio.setFriction(0);
    }

    // 날아가는 카드들
    for (let i = s.flyers.length - 1; i >= 0; i--) {
      const f = s.flyers[i];
      f.x += f.vx * h;
      f.y += f.vy * h;
      f.vx -= sp.flyDrag * f.vx * h;
      f.vy -= sp.flyDrag * f.vy * h;
      f.rot += f.w * h;
      f.bend *= Math.exp(-sp.bendRelaxSpeed * h);
      const travel = f.x * f.dirX + f.y * f.dirY;
      if (!f.revealed && travel >= 1.7 * this.extentAlong(f.dirX, f.dirY)) {
        f.revealed = true;
        audio.thud(0.7);
        haptics.pulse(P.haptic.land);
        this.onEvent?.('land');
        // 덮고 있던 카드가 비켜났다 = 아래 카드가 드러난 순간. 마지막 장이면 아래에 카드가 없다.
        this.fireReveal(f.idx + 1);
      }
      if (Math.hypot(f.x, f.y) > WORLD.viewH) {
        f.revealed = true;
        s.flyers.splice(i, 1);
      }
    }
    this.stepRareCue(h);
    if (this.phase === 'reveal' && s.top >= CARD_COUNT && s.flyers.every((f) => f.revealed)) {
      this.phase = 'done';
      this.onEvent?.('done');
    }
  }

  // ---------------------------------------------------------------- AR·UR 등장 연출
  /**
   * 카드가 **드러난 순간** 한 번 터진다. `stepRareCue` 가 예고편이라면 이건 타격이다.
   *
   * 여기서 하는 일은 세 가지뿐이고, **전부 카드를 안 움직인다** (P.reveal 주석의 규칙 1):
   *   빛  — 진행도 t 만 켠다. 밴드 위치·세기는 writeRS 가 매 스텝 계산해 RS 로 넘긴다.
   *   소리 — audio.revealChime (실시간 합성 원샷)
   *   진동 — 한 번의 펄스. 세기가 없으니 길이로 등급을 낸다.
   *
   * 등급 차이는 `urScale` 하나다. UR 이면 빛·소리·진동이 같은 배율로 커진다.
   */
  private fireReveal(idx: number): void {
    const rv = P.reveal;
    if (!rv.on || idx < 0 || idx >= CARD_COUNT) return;
    const rank = idx < CARD_DRAW.ranks.length ? CARD_DRAW.ranks[idx] : -1;
    if (rank < rv.minRank) return;
    const fx = this.fx;
    fx.idx = idx;
    fx.rank = rank;
    fx.t = 0;
    // AR 이 기준(1배), UR 부터 urScale. 등급이 더 늘어나도 "최고 등급만 특별" 이 유지된다.
    fx.scale = rank >= 3 ? Math.max(0, rv.urScale) : 1;
    audio.revealChime(rv.sound * fx.scale);
    haptics.pulse(Math.round(rv.haptic * fx.scale));
    // 이 카드의 예고는 여기서 끝이다. 안 끊으면 리저가 타격 위로 계속 울린다.
    this.sw.rareCutIdx = Math.max(this.sw.rareCutIdx, idx);
    audio.setRare(0, 0);
    this.onEvent?.('reveal-fx');
  }

  /** 진행도만 민다. 밴드 좌표는 writeRS 가 이 t 에서 유도한다. */
  private stepRevealFx(h: number): void {
    const fx = this.fx;
    if (fx.idx < 0) return;
    fx.t += h;
    if (fx.t >= Math.max(0.05, P.reveal.dur)) {
      fx.idx = -1;
      fx.rank = -1;
      fx.t = 0;
    }
  }

  // ---------------------------------------------------------------- 레어 예고
  /**
   * 뒤에 rareMinRank 이상의 카드가 있으면, 그 카드를 덮은 카드를 **잡는 순간부터**
   * 최강 진동(끊김 없는 연속 펄스)이 울리고 예고음이 고조된다. 다 드러나면 둘 다 멈춘다.
   *
   * 진동은 켜짐/꺼짐뿐이라 세기를 못 올린다(§8). 그래서 진동은 처음부터 최강 고정이고,
   * 고조는 세기가 있는 **소리**가 맡는다. 소리는 iOS 처럼 진동이 없는 기기에도 전달된다.
   */
  private stepRareCue(h: number): void {
    const s = this.sw;
    const hp = P.haptic;
    // 지금 아래 카드를 덮고 있는(잡혀 있거나 날아가는 중인) 카드
    let dx = 0, dy = 0, covering = -1;
    if (s.state !== 'idle') {
      covering = s.top;
      dx = s.px;
      dy = s.py;
    } else if (s.flyers.length) {
      const f = s.flyers[s.flyers.length - 1];
      covering = f.idx;
      dx = f.x;
      dy = f.y;
    }
    if (covering < 0) {
      s.reveal = 0;
      s.revealIdx = -1;
      s.rareT = 0;
      s.rareActive = false;
      s.rareAcc = 999;
      audio.setRare(0, h);
      return;
    }
    // 비켜난 거리 / 그 방향 카드 전체 길이 = 아래 카드가 드러난 비율.
    // 아직 안 움직였으면 0 이지만, rareFrom = 0 이라 잡는 순간부터 조건이 성립한다.
    const dist = Math.hypot(dx, dy);
    const ux = dist > 1e-6 ? dx / dist : 0;
    const uy = dist > 1e-6 ? dy / dist : 1;
    s.reveal = Math.min(1, dist / Math.max(0.01, 2 * this.extentAlong(ux, uy)));
    s.revealIdx = covering + 1;

    const idx = s.revealIdx;
    const rank = idx >= 0 && idx < CARD_DRAW.ranks.length ? CARD_DRAW.ranks[idx] : -1;
    const inRange = s.reveal >= hp.rareFrom && s.reveal < hp.rareTo;
    // 이미 등장 연출이 터진 카드면 예고는 끝났다 (fireReveal 주석).
    const spent = idx >= 0 && idx <= s.rareCutIdx;
    if (!hp.rareOn || rank < hp.rareMinRank || !inRange || spent) {
      // 다 드러났으면 울리던 진동을 즉시 끊는다
      if (s.rareActive) haptics.stop();
      s.rareActive = false;
      s.rareT = 0;
      s.rareAcc = 999; // 조건이 맞는 순간 첫 펄스가 바로 나가도록 채워 둔다
      audio.setRare(0, h);
      return;
    }
    const span = Math.max(1e-3, hp.rareTo - hp.rareFrom);
    const t = Math.max(0, Math.min(1, (s.reveal - hp.rareFrom) / span));
    s.rareT = t;
    if (!s.rareActive) {
      s.rareActive = true;
      this.onEvent?.('rare');
    }
    // 소리는 진행도에 따라 고조된다
    audio.setRare(t, h);
    // 진동은 세기가 없으니 처음부터 최강 — 간격보다 긴 펄스를 겹쳐 쏴 모터를 안 끊는다
    const interval = 1 / Math.max(1, hp.rareRate);
    s.rareAcc += h;
    if (s.rareAcc >= interval) {
      // 나머지는 이월하되, 시작 직후처럼 과하게 밀린 경우엔 버린다.
      // (남은 값을 interval 로 클램프하면 다음 스텝에 곧바로 또 쏴서 두 배로 빨라진다)
      const rest = s.rareAcc - interval;
      s.rareAcc = rest > interval ? 0 : rest;
      haptics.pulse(hp.rarePulse);
      s.rarePulses++;
    }
  }

  // ---------------------------------------------------------------- RS 기록
  private writeRS(): void {
    const r = this.rs;
    const c = this.cut;
    const T = WORLD.cardThickness;
    // 뭉치의 z 중심. 8장이던 시절의 3.5 를 CARD_COUNT 에서 유도한다
    const ZC = (CARD_COUNT - 1) / 2;
    r.topX = this.topX;
    r.topY = this.topY;
    r.topRot = this.topRot;
    r.topVis = this.topVis ? 1 : 0;
    r.bodyY = this.bodyY;
    r.cutFront = c.dir !== 0 ? c.front : -1;
    r.cutStartD = c.startD;
    r.cutDir = c.dir;
    r.cutStartEdgeX = c.startEdgeX;
    r.gapScale = 1 + P.cut.gapVelEffect * Math.abs(this.input.vSlow.x);
    r.bulgeD = c.front + P.cut.bulgeLead;
    r.bulgeAmt = c.completed ? 0 : P.cut.bulgeAmount * c.bulgeSmooth;
    r.tension = c.Rs > 0 ? Math.min(1, c.F / c.Rs) : 0;

    // 금액 텍스트 — 감속하며 오르고, floatFade 를 지나면 선형으로 사라진다. 이징 커브가 아니라
    // 진행도 u 에서 매 스텝 다시 계산한 값이다 (u 는 240Hz 로 적분된 t 에서 나온다).
    const f = this.float;
    if (f.active) {
      const u = Math.min(1, f.t / Math.max(0.05, P.econ.floatDur));
      const fade = Math.min(0.999, Math.max(0, P.econ.floatFade));
      r.floatKey = f.key;
      r.floatX = f.x;
      r.floatY = f.y;
      r.floatDY = P.econ.floatRise * u * (2 - u);
      r.floatA = u <= fade ? 1 : 1 - (u - fade) / (1 - fade);
    } else {
      r.floatKey = 0;
      r.floatDY = 0;
      r.floatA = 0;
    }

    // AR·UR 등장 연출 — 진행도 t 에서 밴드 좌표와 두 세기를 매 스텝 다시 계산한다.
    // 셰이더의 밴드 좌표는 `vUv.y + vUv.x*0.35` 라 0..1.35 를 훑는다 (geometry.ts FRAG).
    const fx = this.fx;
    if (fx.idx >= 0) {
      const rv = P.reveal;
      const bw = Math.max(0.01, rv.bandWidth);
      const u = Math.min(1, fx.t / Math.max(0.05, rv.dur));
      r.fxIdx = fx.idx;
      r.fxRank = fx.rank;
      r.fxWidth = bw;
      // 폭만큼 바깥에서 들어와 반대쪽 바깥으로 빠진다 — 카드 안에서 생기거나 사라지지 않는다.
      r.fxBand = -bw + (1.35 + 2 * bw) * u;
      // 가운데를 지날 때 가장 밝다. 들어오고 나가는 끝이 0 이라 켜짐/꺼짐이 안 보인다.
      r.fxStrength = rv.band * fx.scale * Math.sin(Math.PI * u);
      // 전면 밝아짐은 드러난 순간이 최대고 빠르게 꺼진다 ("탁").
      r.fxFlash = rv.flash * fx.scale * Math.exp(-fx.t / Math.max(0.01, rv.flashDecay));
    } else {
      r.fxIdx = -1;
      r.fxRank = -1;
      r.fxBand = -10;
      r.fxStrength = 0;
      r.fxFlash = 0;
    }

    // 스택
    const o = this.open;
    let riseAvg = 0;
    for (let k = 0; k < CARD_COUNT; k++) riseAvg += o.cardRise[k];
    riseAvg /= CARD_COUNT;
    const depth = Math.max(0.01, P.open.cardStartDepth);
    const prog = this.phase === 'pack' || this.phase === 'cutting' ? 0 : Math.max(0, Math.min(1, 1 + riseAvg / depth));
    r.stackX = 0;
    r.stackY = P.open.stackY;
    r.stackZ = -0.25 + 0.55 * prog;
    const baked = this.phase === 'reveal' || this.phase === 'done';
    r.flipMode = baked ? 1 : 0;
    r.flipTheta = this.flip.theta;
    r.flipTilt = this.flip.tilt;
    r.roll = baked ? this.flip.roll : 0;

    const s = this.sw;
    for (let k = 0; k < CARD_COUNT; k++) {
      r.cbend[k] = 0;
      r.ccontact[k] = 0;
      r.cbdx[k] = 0;
      r.cbdy[k] = 1;
      if (!baked) {
        r.cx[k] = this.spreadX[k];
        r.cy[k] = this.spreadY[k] + o.cardRise[k];
        r.cz[k] = (k - ZC) * T;
        r.crot[k] = this.spreadR[k];
        r.cvis[k] = 1;
      } else {
        r.cx[k] = this.spreadX[k];
        r.cy[k] = -this.spreadY[k];
        r.cz[k] = -(k - ZC) * T;
        r.crot[k] = -this.spreadR[k];
        r.cvis[k] = k >= s.top ? 1 : 0;
        if (k === s.top) {
          r.cx[k] += s.px;
          r.cy[k] += s.py;
          // bend 는 물리(걸림 풀림)에 그대로 쓰이고, 화면에는 bendVisual 배율로만 나간다
          r.cbend[k] = s.bend * P.swipe.bendVisual;
          r.ccontact[k] = s.contactProj;
          r.cbdx[k] = s.bdx;
          r.cbdy[k] = s.bdy;
        }
      }
    }
    if (baked) {
      for (const f of s.flyers) {
        const k = f.idx;
        r.cvis[k] = 1;
        r.cx[k] = this.spreadX[k] + f.x;
        r.cy[k] = -this.spreadY[k] + f.y;
        r.cz[k] = -(k - ZC) * T;
        r.crot[k] = -this.spreadR[k] + f.rot;
        r.cbend[k] = f.bend * P.swipe.bendVisual;
        r.ccontact[k] = f.contactProj;
        r.cbdx[k] = f.dirX;
        r.cbdy[k] = f.dirY;
      }
    }
  }

  snapshot(): void {
    this.prev.copyFrom(this.rs);
  }
}
